import { serverEventSchema, type ServerEvent } from '@bia-auction/shared-schema';
import { useEffect, useRef } from 'react';
import { wsUrl } from './config';
import { ensureAccess } from './token-store';

const RECONNECT_MS = 2_000;

/**
 * Subscribe to a sale's live stream (M3 protocol / ADR 0007). Opens the Hibernatable-WS route
 * with the access token in the query string, subscribes to the given `lot:` rooms (the `sale:`
 * and `user:` streams need no opt-in), validates every frame with `serverEventSchema`, and
 * auto-reconnects (re-subscribing) on drop. `onEvent` is held in a ref so re-renders don't
 * churn the socket; the socket is re-opened when `saleId` **or `authGen`** changes.
 *
 * `authGen` is a fingerprint of the authenticated identity + roles. Firm-scope and JWT roles
 * are verified only at the WS upgrade, so a socket opened with old privileges would keep
 * delivering private events after a token refresh downgraded the viewer. Including `authGen` in
 * the connection lifecycle tears the socket down and re-establishes it (re-verifying the fresh
 * token) whenever authorization claims change.
 */
export function useSaleRealtime(
  saleId: string | null,
  lotRooms: string[],
  onEvent: (event: ServerEvent) => void,
  onReconnect?: () => void,
  authGen = '',
): void {
  const onEventRef = useRef(onEvent);
  onEventRef.current = onEvent;
  const onReconnectRef = useRef(onReconnect);
  onReconnectRef.current = onReconnect;
  const roomsRef = useRef<string[]>(lotRooms);
  roomsRef.current = lotRooms;
  const wsRef = useRef<WebSocket | null>(null);

  useEffect(() => {
    if (!saleId) return;
    let closed = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const connect = async (): Promise<void> => {
      if (closed) return;
      const token = await ensureAccess();
      if (closed) return;
      if (!token) {
        // Refresh temporarily failed (e.g. 429/5xx) — no socket was created, so there's no
        // onclose to retry from. Schedule another attempt ourselves.
        timer = setTimeout(() => void connect(), RECONNECT_MS);
        return;
      }
      const ws = new WebSocket(
        wsUrl(`/sales/${saleId}/realtime?token=${encodeURIComponent(token)}`),
      );
      wsRef.current = ws;
      ws.onopen = () => {
        if (roomsRef.current.length > 0) {
          ws.send(JSON.stringify({ type: 'subscribe', rooms: roomsRef.current }));
        }
        // The protocol doesn't replay missed events, so on *every* open (including the first)
        // trigger a snapshot reconciliation (REST refetch) — an event between the initial query
        // load and this subscription, or during a drop, would otherwise leave the console stale.
        onReconnectRef.current?.();
      };
      ws.onmessage = (ev) => {
        try {
          const parsed = serverEventSchema.safeParse(JSON.parse(ev.data as string));
          if (parsed.success) onEventRef.current(parsed.data);
        } catch {
          // ignore malformed frames
        }
      };
      ws.onclose = () => {
        wsRef.current = null;
        if (!closed) timer = setTimeout(() => void connect(), RECONNECT_MS);
      };
      ws.onerror = () => ws.close();
    };
    void connect();

    return () => {
      closed = true;
      if (timer) clearTimeout(timer);
      wsRef.current?.close();
      wsRef.current = null;
    };
    // Re-open on saleId OR authGen change (an auth downgrade must drop the old-privilege socket).
  }, [saleId, authGen]);

  // Re-subscribe when the set of lot rooms changes on an open socket.
  useEffect(() => {
    const ws = wsRef.current;
    if (ws?.readyState === WebSocket.OPEN && lotRooms.length > 0) {
      ws.send(JSON.stringify({ type: 'subscribe', rooms: lotRooms }));
    }
  }, [lotRooms]);
}
